import React, { useState, useEffect, useRef } from "react"
import _ from "lodash"

import { DateInput, NumberInput, DropdownInput } from "components/table/carousel/inputs"
import { RowAddRemove } from "components/table/carousel/row-add-remove-by-reference"
import { field_attrs } from "components/table/lib/column-field"

import { checkDateSet, checkDateMode } from "data/lib/dates"

import { FormError_s } from "styles/form-styles"
import { MultiRowCarouselState_s } from "components/table/carousel/carousel-styles"

type RentDetail = {
  amt: number | null
  amtType: string
  date: number|string|null
  i: number
}

type RentDetailProps = {
  defaults: {
    rentAmtType: string
  }
  field: string,
  persist: Function
  rowData: {
    rentdetail: {
      items: RentDetail[]
      dateMode: string
      state: string
    }
  }
}

const nois = (xs) => _.map(xs, x => _.omit(x,'i'))

const RentDetailed = (props: RentDetailProps) => {
  const emptyRecord = {
    date: null,
    amt: null,
    amtType: props.defaults['rentAmtType'],
    i: 0
  }
  const rentdetail = props.rowData.rentdetail || {}
  const [items, setItems] = useState<RentDetail[]>([emptyRecord])

  // initial data load
  useEffect(() => {
    if (_.isEmpty(rentdetail.items)) return
    setItems(_.chain(rentdetail.items).map((it,i) => ({...it, i})).sortBy('date').value())
  }, [])

  // persist on local data change
  useEffect(() => {
    if(_.isEqual(items, [emptyRecord])) return
    if(_.isEqual(nois(items), rentdetail.items)) return
    persist(items)
  }, [JSON.stringify(nois(items))]) // limit effect to value difference, rather than reference.


  const update = (ix) => (value, path) => {
    path = [ix, ...path]
    setItems(_.set(_.cloneDeep(items), path, value))
  }

  const persist = (items) => {
    let data = _.cloneDeep(rentdetail)

    // update state and clear non-state fields
    data.state = 'detailed'
    delete data.amt
    delete data.amtType
    delete data.increaseNamedItemId
    delete data.rentNamedItemId

    // display order is implicit, but persisted order is i.  update local i based on sort.
    const ui_items = [...items]
    const db_items = _.sortBy(items, 'date')
    setItems(_.map(ui_items, (it, i) => ({...it, i: _.findIndex(db_items, {i: it.i})})))
    data.items = nois(db_items)

    // update datemode if needed
    if (_.isFinite(_.get(items, '0.date'))) data.dateMode = 'relative'
    else data.dateMode = 'absolute'

    props.persist(data, props.field, props.rowData)
  }

  const params = (ix) => ({
    data: items[ix],
    persist: update(ix)
  })

  const dates = _(items).map('date').filter().value()
  const homogenous = _.isEmpty(dates) || checkDateSet(dates[0].date, dates).homogenous

  const attrs = (ix) => (field) => field_attrs('rentroll', `${props.field}.items.${ix}`)(field)(props.rowData, props)

  return (
    <div>
      { _.map(items, (it, i) => (
          <MultiRowCarouselState_s className="carousel-multi-row" key={`rentdetail-${i}`}>
            <div>
              { i === 0 && <label>Date</label> }
              <DateInput className="automated" attrs={attrs(it.i)} field={['date']} {...params(i)} allowRelative />
            </div>
            <div>
              { i === 0 && <label>Amt</label> }
              <NumberInput className="automated" attrs={attrs(it.i)} field={['amt']}  {...params(i)} readPrecision={4} writePrecision={4} trailingZeros={false}/>
            </div>
            <div>
              { i === 0 && <label>Amt Type</label> }
              <DropdownInput
                  field={['amtType']}
                  className="automated"
                  attrs={attrs(it.i)}
                  options={['% Increase', '% Increase Annually', '% of Market', '% of Market Annually', '/Area/Mo', '/Area/Yr', '/Area/Yr Annually', '/Mo', '/Yr']}
                  {...params(i)}
                  />
            </div>
            <RowAddRemove emptyRecord={{...emptyRecord, i: i+1, amtType: it.amtType} as RentDetail} persist={setItems} row={it} items={items} />
          </MultiRowCarouselState_s>
      ))}
      {!homogenous && <FormError_s>All dates must be either relative or absolute</FormError_s>}
    </div>
  )
}

export default RentDetailed
